import styled, { css } from 'styled-components';
import { rem } from 'polished';
import variables from '@styles/variables';
import Config from '@config';
import IconTooltip from '@molecules/IconTooltip/IconTooltip';
import { StepperTheme, StepperVariant } from './types';

// --- Shared Mixins ---

// Wraps - input +
export const stepperWrapper = css<{ $disabled: boolean }>`
  display: flex;
  align-items: center;
  border-radius: ${rem(22)};

  ${(props) =>
    props.$disabled &&
    css`
      cursor: not-allowed;
    `}
`;

export const showHideAnimation = css<{ $hide: boolean }>`
  transition: opacity 300ms;
  opacity: ${(props) => (props.$hide ? 0 : 1)};
`;

const baseButton = css`
    display: flex;
    align-items: center;
    justify-content: center;
    border: 0;
    border-radius: ${rem(99)};
    cursor: pointer;
  }
`;

export const commonDecreaseButton = css<{ $hide: boolean }>`
  ${baseButton};
  background-color: ${variables.colors.lightGray};
  color: ${variables.colors.darkGray};
  // Fades out when hideWhenEmpty is active and quantity is 0
  ${showHideAnimation};
`;

export const commonIncreaseButton = css`
  ${baseButton};
  // For OK button
  font-family: ${variables.fonts.fontGothamMedium};
  background-color: ${variables.colors.primary};
  color: ${variables.colors.white};

  &:disabled {
    background-color: ${variables.colors.primaryInactive};
  }
`;

// Base styles for the raw <input> element inside the quantity stepper.
// Resets native input styling and centers text between the +/- buttons.
export const commonInput = css`
  text-align: center;
  color: ${variables.colors.lighterBlack};
  font-family: ${variables.fonts.fontGothamMedium};

  // Remove standard input values
  border: none;
  background-color: transparent;

  // Clear sharp edges
  border-radius: ${rem(2)};

  // Vertical padding to match
  padding: ${rem(10)} 0;

  &:disabled {
    color: ${variables.colors.darkGray};
    -webkit-text-fill-color: ${variables.colors.darkGray};
  }

  &:focus {
    box-shadow: inset 0 0 0 ${rem(2)} ${variables.colors.highlight};
  }
`;

// Button sizing. Every variant resolves to one of three size steps (label = the "OK" text,
// pinned so it doesn't inherit an ambient size):
//
//   productSize   36px,        15px label            → "product": product cards + PDP
//   standardSize  28px → 32px, 11px → 13px label     → "cart" & "wishlist": cart rows, search, buy-all, wishlist, add-to-list
//   compactSize   28px,        11px label (no upscale) → mini-cart: the "cart" variant with $minicart

const productSize = css`
  height: ${rem(36)};
  width: ${rem(36)};
  font-size: ${rem(15)};
`;

const compactSize = css`
  height: ${rem(28)};
  width: ${rem(28)};
  font-size: ${rem(11)};
`;

const standardSize = css`
  ${compactSize};

  ${Config.MEDIA.FROM_TABLET_PORTRAIT} {
    height: ${rem(32)};
    width: ${rem(32)};
    font-size: ${rem(13)};
  }
`;

const buttonSize = css<{ $variant: StepperVariant; $minicart?: boolean }>`
  ${({ $variant, $minicart }) => {
    if ($variant === 'product') return productSize;
    return $minicart ? compactSize : standardSize;
  }}
`;

// --- Variant-driven Styled Components ---

export const StyledWrapper = styled.div<{ $variant: StepperVariant; $disabled: boolean; $hasItems?: boolean }>`
  ${stepperWrapper};
  ${({ $variant }) =>
    $variant === 'cart' &&
    css`
      width: fit-content;
    `}

  ${({ $variant, $hasItems }) =>
    $variant === 'product' &&
    css`
      padding: ${rem(4)};
      background-color: ${variables.colors.lighterGray};

      ${$hasItems &&
      css`
        background-color: ${variables.colors.primary};
        color: ${variables.colors.white};
      `}
    `}
`;

export const StyledDecreaseButton = styled.button<{
  $variant: StepperVariant;
  $hide: boolean;
  $hasItems?: boolean;
  $minicart?: boolean;
}>`
  ${commonDecreaseButton};
  ${buttonSize};

  ${({ $variant, $hasItems }) =>
    $variant === 'product' &&
    $hasItems &&
    css`
      background-color: ${variables.colors.primary};
      color: ${variables.colors.white};

      &:disabled {
        background-color: ${variables.colors.primaryInactive};
      }
    `}
`;

export const StyledIncreaseButton = styled.button<{
  $variant: StepperVariant;
  $hasItems?: boolean;
  $isActive?: boolean;
  $minicart?: boolean;
  $theme?: StepperTheme;
}>`
  ${commonIncreaseButton};
  ${buttonSize};

  // product: keep red while confirming an edit (isActive), else inactive gray, when it has items
  ${({ $variant, $hasItems, $isActive }) =>
    $variant === 'product' &&
    $hasItems &&
    css`
      &:disabled {
        background-color: ${$isActive ? variables.colors.primary : variables.colors.primaryInactive};
      }
    `}

  // cart: keep primary red when the user is confirming a quantity edit
  ${({ $variant, $hasItems, $isActive }) =>
    $variant === 'cart' &&
    $isActive &&
    $hasItems &&
    css`
      &:disabled {
        background-color: ${variables.colors.primary};
      }
    `}

  // wishlist: secondary overrides red→gray with dark text; primary keeps white text
  ${({ $variant, $theme }) =>
    $variant === 'wishlist' &&
    $theme === 'secondary' &&
    css`
      color: ${variables.colors.lighterBlack};
      background-color: ${variables.colors.lightGray};

      // Stay gray when disabled at MAX_AMOUNT. Without this, commonIncreaseButton's
      // &:disabled outranks the background-color above on specificity and the button turns pink.
      &:disabled {
        background-color: ${variables.colors.lightGray};
      }

      &:disabled span,
      &:disabled svg {
        color: ${variables.colors.darkerGray} !important;
      }
    `}

  ${({ $variant, $theme }) =>
    $variant === 'wishlist' &&
    $theme === 'primary' &&
    css`
      color: ${variables.colors.white};
    `}
`;

export const StyledInput = styled.input<{
  $variant: StepperVariant;
  $hide: boolean;
  $hasItems?: boolean;
  $minicart?: boolean;
}>`
  ${commonInput};
  ${showHideAnimation};

  // Width comes from the size attribute, pinned to the longest value the field can hold — see
  // QuantityStepper. A ch-based min-width would overshoot, since ch is a digit's width and the
  // unit characters are narrower, leaving dead space even at MAX_AMOUNT.
  min-width: 0;
  overflow: hidden;

  // Small margin between input and buttons
  margin: 0 ${rem(1)};

  // Standard font size (product overrides to a larger size below); pinned so the number
  // doesn't inherit an ambient size.
  font-size: ${rem(13)};

  ${({ $variant }) =>
    $variant === 'product' &&
    css`
      // The product pill spans the card, so the input fills the gap between the two buttons.
      flex: 1;

      font-size: ${rem(15)};
      // Small margin between input and buttons
      margin: 0 ${rem(2)};
    `}

  // Minicart is the tightest placement, it drops the margin
  ${({ $minicart }) =>
    $minicart &&
    css`
      margin: 0;
    `}

  ${({ $variant }) =>
    $variant === 'wishlist' &&
    css`
      &:disabled {
        -webkit-text-fill-color: ${variables.colors.darkerGray};
      }
    `}

  ${({ $variant, $hasItems }) =>
    $variant === 'product' &&
    $hasItems &&
    css`
      color: ${variables.colors.white};

      &:disabled {
        -webkit-text-fill-color: ${variables.colors.white};
      }
    `}
`;

// --- Shared Styled Components ---

export const StyledOutOfStock = styled.div<{ $product?: boolean }>`
  display: flex;
  align-items: center;

  ${(props) =>
    props.$product &&
    css`
      // When its a button
      justify-content: center;
      border-radius: ${rem(22)};
      padding: ${rem(4)};
      height: ${rem(44)};
      background-color: ${variables.colors.lighterGray};
    `}
`;

export const StyledIconToolTip = styled(IconTooltip)`
  top: 0;
  margin-top: 0;
  left: 0;
`;
